import { Dispatch, SetStateAction } from 'react';
import { createJSONStorage } from 'zustand/middleware';
import { formatPhoneNumberE164 } from '@frontend/phone-numbers';
import { createShallowStore, createStoreWithPersistAndSubscribe } from '@frontend/store';
import { genUUIDV4 } from '@frontend/string';
import { MiniChatProps } from './types';

export const moveChat = (chats: MiniChatContext[], chatId: string, destinationIndex: number | 'end' | 'start') => {
  const { chatToMove, otherChats } = chats.reduce<{
    chatToMove?: MiniChatContext;
    otherChats: MiniChatContext[];
  }>(
    (acc, chat) => {
      if (chat.chatId === chatId) acc.chatToMove = chat;
      else acc.otherChats.push(chat);
      return acc;
    },
    { otherChats: [] }
  );

  if (destinationIndex === 'start') {
    return [...(chatToMove ? [chatToMove] : []), ...otherChats];
  } else if (destinationIndex === 'end') {
    return [...otherChats, ...(chatToMove ? [chatToMove] : [])];
  } else {
    const leftChats = otherChats.slice(0, destinationIndex);
    const rightChats = otherChats.slice(destinationIndex);
    return [...leftChats, ...(chatToMove ? [chatToMove] : []), ...rightChats];
  }
};

export const MINI_CHAT_STORE_KEY = 'MiniChatStore';

export const chatIsAvatarVariant = (chatIndex: number, currentAvatarChatCount: number, activeChatIndex: number) =>
  chatIndex !== -1 &&
  chatIndex < currentAvatarChatCount + (activeChatIndex >= 0 && activeChatIndex < currentAvatarChatCount ? 1 : 0);

export type MiniChatContext = Pick<
  MiniChatProps,
  | 'groupId'
  | 'personPhone'
  | 'contextPersonId'
  | 'unreadCount'
  | 'threadId'
  | 'focusThreadItemData'
  | 'locationPhone'
  | 'isNew'
> & {
  chatId: string;
  possiblePersonPhones: string[];
};

type MiniChatStore = {
  chats: MiniChatContext[];
  avatarChatCount: number;
  setAvatarChatCount: Dispatch<SetStateAction<number>>;
  overflowChatCount: number;
  setOverflowChatCount: Dispatch<SetStateAction<number>>;
  activeChatId: string | undefined;
  rolledUp: boolean;
  setRolledUp: Dispatch<SetStateAction<boolean>>;
  addChat: (chatContext: Omit<MiniChatContext, 'possiblePersonPhones' | 'chatId' | 'sizeVariant'>) => void;
  removeChat: (chatId: string) => void;
  removeChats: (chatIds: string[]) => void;
  openChat: (chatId: string) => void;
  minimizeChat: () => void;
  setChatPossiblePersonPhones: (chatId: string, newPossiblePersonPhones: string[]) => void;
  setChatContextPersonId: (personId: string, chatId: string) => void;
  moveChat: (chatId: string, destinationIndex: number) => void;
  incrementThreadUnreadCount: (threadId: string) => void;
  resetThreadUnreadCount: (threadId: string) => void;
  errorBoundaryActive: boolean;
  setErrorBoundaryActive: Dispatch<SetStateAction<boolean>>;
  showErrorBoundaryAlert: boolean;
  setShowErrorBoundaryAlert: Dispatch<SetStateAction<boolean>>;
  reset: () => void;
  updateChat: (chatId: string, newContext: Partial<MiniChatContext>) => void;
};

export const useMiniChatStore = createStoreWithPersistAndSubscribe<MiniChatStore>(
  (set) => ({
    chats: [],
    avatarChatCount: 0,
    setAvatarChatCount: (newVal) => {
      set((state) => {
        const calculatedValue = typeof newVal === 'function' ? newVal(state.avatarChatCount) : newVal;
        const indexOfActiveChat = state.chats.findIndex((chat) => chat.chatId === state.activeChatId);

        return {
          ...state,
          chats:
            state.activeChatId && indexOfActiveChat < calculatedValue
              ? moveChat(state.chats, state.activeChatId, calculatedValue)
              : state.chats,
          avatarChatCount: calculatedValue,
        };
      });
    },
    overflowChatCount: 0,
    setOverflowChatCount: (newVal) => {
      set((state) => {
        const calculatedValue = typeof newVal === 'function' ? newVal(state.overflowChatCount) : newVal;
        return {
          ...state,
          overflowChatCount: Math.min(state.chats.length - (state.activeChatId ? 1 : 0), calculatedValue),
        };
      });
    },
    activeChatId: undefined,
    rolledUp: false,
    setRolledUp: (newVal) => {
      set((state) => {
        const calculatedValue = typeof newVal === 'function' ? newVal(state.rolledUp) : newVal;
        return {
          ...state,
          rolledUp: calculatedValue,
          showErrorBoundaryAlert: state.errorBoundaryActive ? true : state.showErrorBoundaryAlert,
        };
      });
    },
    addChat: (chatContext) => {
      set((state) => {
        const existingChatIndex = state.chats.findIndex(
          ({ threadId, possiblePersonPhones }) =>
            chatContext.threadId === threadId ||
            possiblePersonPhones.some(
              (personPhone) => formatPhoneNumberE164(personPhone) === formatPhoneNumberE164(chatContext.personPhone)
            )
        );
        const existingChat = existingChatIndex === -1 ? undefined : state.chats[existingChatIndex];

        if (existingChat) {
          const newChats = chatIsAvatarVariant(
            existingChatIndex,
            state.avatarChatCount,
            state.chats.findIndex(({ chatId }) => chatId === state.activeChatId)
          )
            ? moveChat(state.chats, existingChat.chatId, 'end')
            : state.chats;
          const chatsWithUpdatedThread = newChats.map((chat) =>
            chat.chatId === existingChat.chatId
              ? {
                  ...chat,
                  threadId: chatContext.threadId,
                  personPhone: chatContext.personPhone,
                  contextPersonId: chatContext.contextPersonId ?? chat.contextPersonId,
                  focusThreadItemData: chatContext.focusThreadItemData ?? chat.focusThreadItemData,
                  locationPhone: chatContext.locationPhone ?? chat.locationPhone,
                  groupId: chatContext.groupId ?? chat.groupId,
                  isNew: chatContext.isNew ?? chat.isNew,
                }
              : chat
          );
          return {
            ...state,
            rolledUp: false,
            activeChatId: existingChat.chatId,
            chats: chatsWithUpdatedThread,
            showErrorBoundaryAlert: state.errorBoundaryActive ? true : state.showErrorBoundaryAlert,
          };
        } else {
          const newId = genUUIDV4();
          return {
            ...state,
            activeChatId: newId,
            rolledUp: false,
            chats: [
              ...state.chats,
              {
                ...chatContext,
                possiblePersonPhones: [],
                chatId: newId,
              } satisfies MiniChatContext,
            ],
            showErrorBoundaryAlert: state.errorBoundaryActive ? true : state.showErrorBoundaryAlert,
          };
        }
      });
    },
    removeChat: (chatId: string) => {
      set((state) => ({
        ...state,
        chats: state.chats.filter((chat) => chatId !== chat.chatId),
        activeChatId: state.activeChatId === chatId ? undefined : state.activeChatId,
        showErrorBoundaryAlert: state.errorBoundaryActive ? true : state.showErrorBoundaryAlert,
      }));
    },
    removeChats: (chatIds: string[]) => {
      set((state) => ({
        ...state,
        chats: state.chats.filter((chat) => !chatIds.includes(chat.chatId)),
        activeChatId: state.activeChatId && chatIds.includes(state.activeChatId) ? undefined : state.activeChatId,
        showErrorBoundaryAlert: state.errorBoundaryActive ? true : state.showErrorBoundaryAlert,
      }));
    },
    openChat: (chatId: string) => {
      set((state) => {
        const chatIndex = state.chats.findIndex((chat) => chat.chatId === chatId);

        return {
          ...state,
          activeChatId: chatId,
          chats: chatIsAvatarVariant(
            chatIndex,
            state.avatarChatCount,
            state.chats.findIndex(({ chatId }) => chatId === state.activeChatId)
          )
            ? moveChat(state.chats, chatId, 'end')
            : state.chats,
          showErrorBoundaryAlert: state.errorBoundaryActive ? true : state.showErrorBoundaryAlert,
        };
      });
    },
    minimizeChat: () => {
      set((state) => ({
        ...state,
        activeChatId: undefined,
        showErrorBoundaryAlert: state.errorBoundaryActive ? true : state.showErrorBoundaryAlert,
      }));
    },
    setChatPossiblePersonPhones: (chatId: string, newVal: string[]) => {
      set((state) => ({
        ...state,
        chats: state.chats.map((chat) =>
          chatId === chat.chatId ? ({ ...chat, possiblePersonPhones: newVal } satisfies MiniChatContext) : chat
        ),
      }));
    },
    setChatContextPersonId: (contextPersonId: string, chatId: string) => {
      set((state) => ({
        ...state,
        chats: state.chats.map<MiniChatContext>((chat) =>
          chatId === chat.chatId ? { ...chat, contextPersonId } : chat
        ),
      }));
    },
    moveChat: (chatId: string, destinationIndex: 'start' | 'end' | number) => {
      set((state) => ({
        ...state,
        chats: moveChat(state.chats, chatId, destinationIndex),
        showErrorBoundaryAlert: state.errorBoundaryActive ? true : state.showErrorBoundaryAlert,
      }));
    },
    incrementThreadUnreadCount: (threadId: string) => {
      set((state) => ({
        ...state,
        chats: state.chats.map((chat) =>
          chat.threadId === threadId ? { ...chat, unreadCount: chat.unreadCount + 1 } : chat
        ),
      }));
    },
    resetThreadUnreadCount: (threadId: string) => {
      set((state) => ({
        ...state,
        chats: state.chats.map((chat) => (chat.threadId === threadId ? { ...chat, unreadCount: 0 } : chat)),
      }));
    },
    errorBoundaryActive: false,
    setErrorBoundaryActive: (newValue) => {
      set((state) => {
        const calculatedValue = typeof newValue === 'function' ? newValue(state.errorBoundaryActive) : newValue;
        return {
          ...state,
          errorBoundaryActive: calculatedValue,
          showErrorBoundaryAlert: calculatedValue ? state.showErrorBoundaryAlert : false,
        };
      });
    },
    showErrorBoundaryAlert: false,
    setShowErrorBoundaryAlert: (newValue) => {
      set((state) => {
        if (!state.errorBoundaryActive) {
          // If error boundary isn't active, never show the alert
          return {
            ...state,
            showErrorBoundaryAlert: false,
          };
        }
        const calculatedValue = typeof newValue === 'function' ? newValue(state.showErrorBoundaryAlert) : newValue;
        return {
          ...state,
          showErrorBoundaryAlert: calculatedValue,
        };
      });
    },
    reset: () => {
      set((state) => {
        return {
          ...state,
          chats: [],
          avatarChatCount: 0,
          overflowChatCount: 0,
          activeChatId: undefined,
          rolledUp: false,
          errorBoundaryActive: false,
          showErrorBoundaryAlert: false,
        };
      });
    },
    updateChat: (chatId, newContext) => {
      set((state) => {
        return {
          ...state,
          chats: state.chats.map((chat) => (chat.chatId === chatId ? { ...chat, ...newContext } : chat)),
        };
      });
    },
  }),
  { name: MINI_CHAT_STORE_KEY, version: 4, storage: createJSONStorage(() => sessionStorage) },
  { name: 'MiniChats', trace: true }
);

export const useMiniChatShallowStore = createShallowStore(useMiniChatStore);
